<template>
  <label :for="searchId">Item</label>
  <input
    type="text"
    :id="searchId"
    :name="`${searchId || 'tariff'}-lookup`"
    class="autocomplete"
    autocomplete="off"
    placeholder="Search item"
    v-model="searchItem"
    @focus="handleClear"
    :disabled="disabled"
  />
</template>
  
<script>
import { ref, onMounted, onBeforeUnmount, watch, watchEffect } from "vue";
import nativeUi from "@/ui/native-ui";

export default {
  props: [
    "inputId",
    "data",
    "startAgain",
    "startEdit",
    "disabled",
    "appendDropdownToBody",
    "forceDropdownDown",
    "dropdownClass",
  ],
  emits: ["selected", "searching", "clear"],
  setup(props, context) {
    const searchItem = ref(null);
    const searchId = ref(props.inputId);

    // clear field
    const handleClear = () => {
      searchItem.value = null;
      context.emit("searching", {
        item: null,
      });
    };

    // searching feedbacks
    watchEffect(() => {
      if (searchItem.value) {
        context.emit("searching", {
          item: searchItem.value,
        });
      }
    });

    // clear search input
    watchEffect(() => {
      if (props.startAgain) {
        searchItem.value = null;
        context.emit("clear", {
          item: false, // prevent an infinity loop
        });
      }
    });

    // edit
    watchEffect(() => {
      if (props.startEdit) {
        searchItem.value = props.startEdit;
        context.emit("clear", {
          item: false, // prevent an infinity loop
        });
      }
    });

    const applyDropdownClass = (instance) => {
      if (!props.dropdownClass || !instance?.container) {
        return;
      }

      props.dropdownClass.split(" ").forEach((className) => {
        if (className) {
          instance.container.classList.add(className);
        }
      });
    };

    const appendDropdownToBody = (instance) => {
      if (!props.appendDropdownToBody || !instance?.container) {
        return;
      }

      if (instance.container.parentNode != document.body) {
        document.body.appendChild(instance.container);
      }
    };

    const forceDropdownDown = (instance) => {
      if (!props.forceDropdownDown || !instance?.dropdown || instance.dropdown._easyHmoForceDown) {
        return;
      }

      const dropdown = instance.dropdown;
      const getDropdownPosition = dropdown._getDropdownPosition.bind(dropdown);

      dropdown._getDropdownPosition = function () {
        const positionInfo = getDropdownPosition();
        const triggerBounds = this.el.getBoundingClientRect();
        const dropdownBounds = this.dropdownEl.getBoundingClientRect();
        const availableHeight = window.innerHeight - triggerBounds.bottom - 16;

        return {
          ...positionInfo,
          y: triggerBounds.top - dropdownBounds.top + triggerBounds.height,
          verticalAlignment: "top",
          height: Math.min(positionInfo.height, Math.max(120, availableHeight)),
        };
      };

      dropdown._easyHmoForceDown = true;
    };

    const tuneAutocompleteDropdown = (instance) => {
      applyDropdownClass(instance);
      appendDropdownToBody(instance);
      forceDropdownDown(instance);
    };

    // autocomlete function
    const initializeAutocomplete = () => {
      const elem = document.getElementById(searchId.value);
      const instance = nativeUi.Autocomplete.init(elem, {
        data: props.data,
        onAutocomplete: function (val) {
          searchItem.value = val;
          context.emit("selected", { item: val });
        },
        minLength: 3,
      });

      tuneAutocompleteDropdown(instance);
    };

    // update autocomplete data function
    const updateAutocompleteData = (newVal) => {
      // update action
      const elem = document.getElementById(searchId.value);
      const instance = nativeUi.Autocomplete.getInstance(elem);

      // if empty (fail quietly)
      if (instance) {
        tuneAutocompleteDropdown(instance);
        instance.updateData(newVal);
      }
    };

    // watch for changes
    watch(props, (newVal) => {
      updateAutocompleteData(newVal.data);
    });

    // initiate autocomplete
    onMounted(() => {
      initializeAutocomplete();
    });

    onBeforeUnmount(() => {
      const elem = document.getElementById(searchId.value);
      const instance = elem ? nativeUi.Autocomplete.getInstance(elem) : null;

      if (instance) {
        if (instance.dropdown) {
          instance.dropdown.destroy();
        }

        instance.destroy();
      }
    });

    return {
      handleClear,
      searchItem,
      searchId,
    };
  },
};
</script>
<style>
</style>
  
